import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { useAcknowledgeReceipt } from '../../api/hooks';
import type { RetailerOrderSummary } from '../../api/types';
import { useSession } from '../../auth/session';
import { Button, Chip, Modal } from '../../ui';

/**
 * R-06 — a shipment as the retailer sees it: Shipped, then Delivered once THEY confirm receipt.
 *
 * Delivery is never inferred from dispatch, a page visit or time passing; the only way to Delivered is
 * the retailer's own confirmation (SH-07), checked again on the server. The timeline shows only steps
 * the workflow really has: the order is confirmed (it could not have shipped otherwise), the shipment
 * is shipped, it is awaiting the retailer's confirmation, then delivered. The button appears only while
 * the shipment is eligible (shipped, not delivered) and the user may act; it asks first, and the
 * mutation is disabled while in flight so a double click is one request.
 */
export function ShipmentStatus({ shipment }: { shipment: RetailerOrderSummary['shipments'][number] }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const ack = useAcknowledgeReceipt();
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const may = me?.kind === 'retailer' && me.permissions.includes('shop.order_view');
  const delivered = shipment.status === 'DELIVERED';

  const steps: Array<{ key: string; label: string; state: 'done' | 'now' | 'todo' }> = [
    { key: 'confirmed', label: t('shop.orders.stepConfirmed'), state: 'done' },
    { key: 'shipped', label: t('shop.orders.shipped'), state: 'done' },
    { key: 'awaiting', label: t('shop.orders.stepAwaiting'), state: delivered ? 'done' : 'now' },
    { key: 'delivered', label: t('shop.orders.delivered'), state: delivered ? 'done' : 'todo' },
  ];

  function confirm() {
    setError(null);
    ack.mutate(shipment.id, {
      onSuccess: () => setAsking(false),
      // The server's own sentence (already plain: not the retailer's shipment, not dispatched yet, ...).
      onError: (e) => setError(e instanceof ApiError ? e.message : t('shop.orders.acknowledgeFailed')),
    });
  }

  return (
    <div>
      {delivered ? <Chip tone="done">{t('shop.orders.delivered')}</Chip> : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-2)', flexWrap: 'wrap' }}>
          <Chip tone="run">{t('shop.orders.shipped')}</Chip>
          {may ? (
            <Button size="sm" onClick={() => { setError(null); setAsking(true); }}>{t('shop.orders.acknowledge')}</Button>
          ) : null}
        </div>
      )}
      {asking ? (
        <Modal
          title={t('shop.orders.confirmTitle')}
          onClose={() => { if (!ack.isPending) setAsking(false); }}
          footer={(
            <>
              <Button tone="ghost" disabled={ack.isPending} onClick={() => setAsking(false)}>{t('shop.orders.confirmNo')}</Button>
              <Button disabled={ack.isPending} onClick={confirm}>
                {ack.isPending ? t('shop.orders.acknowledging') : t('shop.orders.confirmYes')}
              </Button>
            </>
          )}
        >
          <ol className="mk-steps" aria-label={t('shop.orders.timelineLabel')}>
            {steps.map((s) => (
              <li key={s.key} className={`mk-step mk-step-${s.state}`} aria-current={s.state === 'now' ? 'step' : undefined}>
                {s.label}
              </li>
            ))}
          </ol>
          <p>{t('shop.orders.confirmBody')}</p>
          {error ? <div role="alert" className="mk-dim">{error}</div> : null}
        </Modal>
      ) : null}
    </div>
  );
}
